import axios from 'axios';
import config from '../config';
import { format } from 'date-fns';
import authService from './authService';
import api from './apiClient'; // 分割トークン対応のapiClientを使用

// configからAPIのベースURLを取得
const API_BASE_URL = config.API_BASE_URL || 'http://localhost:5000';
console.log('設定されたAPI_BASE_URL:', API_BASE_URL);

// Orderインターフェースを定義
export interface Order {
  id: string | number;
  customer_id: string | number;
  cast_id?: string | number;
  driver_id?: string | number;
  user_id?: string | number;
  pickup_address?: string;
  delivery_address?: string;
  room_number?: string; // 部屋番号（オーダー後に判明する場合が多い）
  notes?: string;
  status?: string;
  pickup_date?: string | null;
  pickup_time?: string;
  delivery_time?: string;
  delivery_date?: string | null;
  description?: string;
  priority?: number;
  created_at: string;
  updated_at?: string | null;
  completed_at?: string | null;
  cancelled_at?: string | null;
  order_number?: string;
  order_type?: string;
  course?: string;
  options?: any;
  customer_name?: string;
  customer_phone?: string;
  assigned_user?: string;
  error?: string; // エラー情報を追加
  special_instructions?: string;
  cast_ids?: string[]; // 担当キャストのIDリスト
  cast_name?: string | string[];
  course_duration?: number;
  course_name?: string;
  castback?: number; // キャストバック
  grand_total?: number; // 総額
  revenue?: number;
  discount?: number;
  service_time?: number;
  [key: string]: any; // その他のプロパティも許容
}

// モックオーダーデータ
const mockOrders = [
  { 
    id: 'mock-1', 
    customer_id: '1',
    cast_id: '1',
    pickup_address: '東京都渋谷区',
    delivery_address: '東京都新宿区',
    notes: 'テストオーダー',
    status: 'pending',
    pickup_date: format(new Date(), 'yyyy-MM-dd'),
    pickup_time: '14:00',
    delivery_time: '15:00',
    created_at: new Date().toISOString()
  },
  { 
    id: 'mock-2', 
    customer_id: '2',
    cast_id: '1',
    pickup_address: '東京都品川区',
    delivery_address: '東京都目黒区',
    notes: 'テストオーダー2',
    status: 'in_progress',
    pickup_date: format(new Date(), 'yyyy-MM-dd'),
    pickup_time: '16:00',
    delivery_time: '17:00',
    created_at: new Date().toISOString()
  }
];

// 認証ヘッダーを取得する関数
const getAuthHeader = (): Record<string, string> => {
  // authServiceからトークンを取得（より信頼性が高い）
  const token = authService.getToken();
  
  if (!token) {
    console.warn('認証トークンが見つかりません');
    return {};
  }
  
  // 標準的なBearer認証を使用
  console.log('orderService - Bearer認証を使用', { tokenLength: token.length });
  
  // グローバルヘッダーも更新（念のため）
  axios.defaults.headers.common['Authorization'] = `Bearer ${token}`;
  
  const headers: Record<string, string> = {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
    'Accept': 'application/json',
    'Cache-Control': 'no-cache',
    'Pragma': 'no-cache'
  };
  
  // PHP-FPM用の分割トークン設定（長いトークンの場合）
  if (token.length > 200) {
    const midPoint = Math.floor(token.length / 2);
    const part1 = token.substring(0, midPoint);
    const part2 = token.substring(midPoint);
    
    headers['X-Auth-Token-Part1'] = part1;
    headers['X-Auth-Token-Part2'] = part2;
    
    console.log('orderService - 長いトークンを分割して送信:', {
      totalLength: token.length,
      part1Length: part1.length,
      part2Length: part2.length
    });
  }
  
  return headers;
};

// API URLを構築する関数
const buildApiUrl = (endpoint: string, queryParams?: Record<string, any>): string => {
  // API_BASE_URLからパス部分を除去して純粋なベースURLを取得
  let baseUrl = API_BASE_URL;
  
  // URLが正しい形式かチェック
  if (baseUrl.includes('://')) {
    // URLのプロトコル部分とホスト部分を分離
    const urlParts = baseUrl.split('://');
    if (urlParts.length === 2) {
      const protocol = urlParts[0];
      let host = urlParts[1];
      
      // ホスト部分に/apiが含まれているかチェック
      if (host.includes('/api')) {
        console.log('API_BASE_URLに/apiが含まれています');
        // /api/で終わっている場合、そのまま使用
        if (host.endsWith('/api/')) {
          console.log('API_BASE_URLは/api/で終わっています');
        } 
        // /apiで終わっている場合、/を追加
        else if (host.endsWith('/api')) {
          host = `${host}/`;
          console.log('API_BASE_URLは/apiで終わっているため、/を追加:', host);
        }
        // それ以外の場合は、/api/を適切に処理
        else {
          console.log('API_BASE_URLの/api/を処理します');
          if (!host.includes('/api/')) {
            host = host.replace('/api', '/api/');
          }
        }
      } 
      // /apiを含まない場合、/api/を追加
      else {
        if (host.endsWith('/')) {
          host = `${host}api/`;
        } else {
          host = `${host}/api/`;
        }
        console.log('API_BASE_URLに/api/を追加:', host);
      }
      
      // 修正したURLを再構築
      baseUrl = `${protocol}://${host}`;
    }
  }
  
  // エンドポイントの先頭の/を削除（ある場合）
  const cleanEndpoint = endpoint.startsWith('/') ? endpoint.substring(1) : endpoint;
  
  // 最終的なURLを構築
  let url = `${baseUrl}${cleanEndpoint}`;
  
  // クエリパラメータがある場合、それらを追加（最適化版）
  if (queryParams && Object.keys(queryParams).length > 0) {
    const queryString = Object.entries(queryParams)
      .filter(([_, value]) => value !== undefined && value !== null && value !== '')
      .map(([key, value]) => {
        let formattedValue: string;
        if (value instanceof Date) {
          // Dateオブジェクトは 'yyyy-MM-dd' 形式にフォーマット
          formattedValue = format(value, 'yyyy-MM-dd');
        } else {
          formattedValue = String(value);
        }
        return `${encodeURIComponent(key)}=${encodeURIComponent(formattedValue)}`;
      })
      .join('&');
    
    if (queryString) {
      url = `${url}${url.includes('?') ? '&' : '?'}${queryString}`;
      console.log('最適化されたクエリパラメータを追加:', queryString);
    }
  }
  
  console.log('構築されたAPI URL:', url);
  return url;
};

// API URLを構築する（二重スラッシュを防止）
const buildUrl = (path: string): string => {
  // パスが/apiで始まるか確認
  const apiPath = path.startsWith('/api') ? path : `/api${path.startsWith('/') ? path : `/${path}`}`;
  
  // ベースURLが/apiで終わる場合は調整
  if (API_BASE_URL.endsWith('/api')) {
    return `${API_BASE_URL}${apiPath.substring(4)}`;
  }
  
  return `${API_BASE_URL}${apiPath}`;
};

// 個々のオーダーデータを安全に処理する関数
function processOrderData(order: any): Order {
  try {
    console.log('処理前のオーダーデータ:', order);

    if (!order) {
      throw new Error('オーダーデータが無効です');
    }

    // オーダーIDの確認（必須項目）
    if (!order.id) {
      console.error('オーダーIDがありません:', order);
      return {
        id: Math.random().toString(36).substring(7), // ランダムIDを生成
        customer_id: order.customer_id || '0',
        created_at: new Date().toISOString(),
        order_number: order.order_number || 'unknown',
        error: 'IDがありません'
      };
    }

    // 日付フィールドを安全に処理する関数
    const processDateField = (dateValue: any): string | null => {
      if (!dateValue) return null;
      
      if (typeof dateValue === 'string') {
        // ISO形式かどうかチェック
        if (dateValue.includes('T')) {
          try {
            const date = new Date(dateValue);
            if (!isNaN(date.getTime())) {
              return dateValue; // 有効なISO形式の日付
            }
          } catch (e) {
            console.error('日付変換エラー(ISO):', e, dateValue);
          }
        }
        
        // YYYY-MM-DD形式かどうかチェック
        if (dateValue.match(/^\d{4}-\d{2}-\d{2}$/)) {
          return dateValue; // YYYY-MM-DD形式
        }
        
        // HH:MM形式かどうかチェック
        if (dateValue.match(/^\d{1,2}:\d{2}$/)) {
          return dateValue; // HH:MM形式の時刻
        }
      }
      
      console.warn('不明な日付形式:', dateValue);
      return String(dateValue); // とりあえず文字列に変換
    };

    // 基本的なデータ構造を確保
    const processed = {
      id: order.id,
      customer_id: order.customer_id || '0',
      order_number: order.order_number || `OR-${Math.random().toString(36).toUpperCase().substring(2, 10)}`,
      customer_name: order.customer_name || (order.customer ? (order.customer.name || '') : ''),
      customer_phone: order.customer_phone || (order.customer ? (order.customer.phone || '') : ''),
      pickup_address: order.pickup_address || '',
      delivery_address: order.delivery_address || '',
      status: order.status || 'pending',
      description: order.description || '',
      notes: order.notes || '',
      user_id: order.user_id || order.cast_id,
      driver_id: order.driver_id || null,
      pickup_date: processDateField(order.pickup_date),
      delivery_date: processDateField(order.delivery_date),
      pickup_time: order.pickup_time || '',
      delivery_time: order.delivery_time || '',
      entry_time: processDateField(order.entry_time),
      entry_time_value: order.entry_time_value || null,
      order_type: order.order_type || 'free',
      course: order.course || '',
      course_duration: order.course_duration ?? null,
      service_time: order.service_time ?? 0,
      option_details: order.option_details || [],
      castback: order.castback ?? 0,
      revenue: order.revenue ?? 0,
      grand_total: order.grand_total ?? 0,
      options: order.options || [],
      created_at: order.created_at || new Date().toISOString(),
      updated_at: processDateField(order.updated_at),
      completed_at: processDateField(order.completed_at),
      cancelled_at: processDateField(order.cancelled_at),
      priority: order.priority || 0,
      special_instructions: order.special_instructions || '',
      assigned_user: order.assigned_user || ''
    };
    
    console.log('処理後のオーダー:', processed);
    return processed;
  } catch (err) {
    console.error('オーダーデータの処理中にエラーが発生:', err, order);
    // エラーが発生した場合でも最低限のデータを返す
    return {
      id: order?.id || Math.random().toString(36).substring(7),
      customer_id: order?.customer_id || '0',
      created_at: new Date().toISOString(),
      order_number: order?.order_number || 'unknown',
      error: 'データ処理エラー: ' + (err instanceof Error ? err.message : String(err))
    };
  }
}

// APIリクエストを送信する共通関数
const sendRequest = async (url: string, options: any = {}) => {
  try {
    // 毎回認証ヘッダーを取得（最新のトークンを使用）
    const authHeaders = getAuthHeader();
    
    // オプションにヘッダーを設定
    const requestOptions = {
      ...options,
      headers: {
        ...options.headers,
        ...authHeaders
      },
    };
    
    console.log('API URL:', url);
    console.log('認証ヘッダー設定:', {
      hasAuth: !!authHeaders.Authorization,
      hasSplitToken: !!(authHeaders['X-Auth-Token-Part1'] && authHeaders['X-Auth-Token-Part2'])
    });
    
    return await axios(url, requestOptions);
  } catch (error) {
    console.error('APIリクエストエラー:', error);
    throw error;
  }
};

// オーダー一覧を取得
export const getOrders = async (params?: { 
  start_date?: string;
  end_date?: string;
  cast_id?: string;
  user_id?: string | number;
}) => {
  try {
    console.log('オーダー取得を開始...', params);
    
    // buildApiUrl関数を使用してURLを構築
    const url = buildApiUrl('orders', params);
    console.log('API URL:', url);
    
    // トークンを取得
    const token = localStorage.getItem('token');
    if (!token) {
      console.warn('認証トークンが見つかりません');
      return [];
    }
    
    // apiClientを使用（分割トークン対応）
    const response = await api.get(url, {
      withCredentials: true
    });
    
    console.log('オーダーデータを受信:', response.data);
    
    // オブジェクト全体を返すように修正
    return response.data;
    /*
    if (Array.isArray(response.data)) {
      return response.data.map(processOrderData);
    } else if (response.data.orders && Array.isArray(response.data.orders)) {
      return response.data.orders.map(processOrderData);
    } else {
      console.error('予期しないレスポンス形式:', response.data);
      return [];
    }
    */
  } catch (error) {
    console.error('オーダー取得エラー:', error);
    if (axios.isAxiosError(error)) {
      console.error('エラーの詳細:', {
        status: error.response?.status,
        data: error.response?.data,
        message: error.message
      });
    }
    return [];
  }
};

export const getOrder = async (id: string | number): Promise<Order> => {
  if (!id) {
    throw new Error('オーダーIDが指定されていません。');
  }
  console.log(`個別のオーダーを取得します: ${id}`);
  
  try {
    // 実際のAPIリクエスト
    const response = await api.get<Order>(`/orders/${id}`);
    console.log('APIから個別のオーダーデータを取得しました:', response);

    if (response && response.data && response.data.id) {
      return processOrderData(response.data);
    } else {
      console.error('APIからのオーダーデータが無効です:', response);
      throw new Error('APIからのオーダーデータが無効です。');
    }
  } catch (error) {
    console.error(`オーダーデータの取得に失敗しました (ID: ${id}):`, error);
    if (axios.isAxiosError(error)) {
      console.error('Axiosエラー詳細:', {
        message: error.message,
        response: error.response?.data,
        status: error.response?.status
      });
      throw new Error(`サーバーからの応答エラー: ${error.response?.status || '不明'}`);
    }
    throw new Error('オーダーデータの取得中に不明なエラーが発生しました。');
  }
};


// 既存のオーダーサービスオブジェクト
export const orderService = {
  async getOrders(params?: { 
    start_date?: string;
    end_date?: string;
    cast_id?: string;
    user_id?: string | number;
  }) {
    try {
      console.log('オーダー情報を取得中...', params);
      
      // パラメータがある場合は新しい実装を使用
      if (params) {
        return getOrders(params);
      }
      
      // トークンがない場合は開発環境ではモックデータを返す
      const token = localStorage.getItem('token');
      if (!token && process.env.NODE_ENV === 'development') {
        console.log('⚠️ 認証トークンがないため、モックデータを返します');
        return { orders: mockOrders };
      }
      
      // buildApiUrl関数を使用してURLを構築
      const apiUrl = buildApiUrl('orders');
      console.log('API URL:', apiUrl);
      
      // apiClientを使用（分割トークン対応）
      const response = await api.get(apiUrl, {
        withCredentials: true
      });
      
      console.log('APIからの生レスポンス:', response.data);

      // デバッグのため、変換をスキップして生データを返す
      return response.data;
    } catch (error) {
      console.error('オーダーの取得に失敗:', error);
      
      // エラー詳細をログに記録
      if (axios.isAxiosError(error)) {
        console.error('エラーの詳細:', error.response?.data);
        console.error('ステータスコード:', error.response?.status);
        console.error('エラーメッセージ:', error.message);
        console.error('リクエストURL:', error.config?.url);
      }
      
      // 開発環境ではモックデータを返す
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ エラー発生のため、モックデータを返します');
        return { orders: mockOrders };
      }
      
      // エラーオブジェクトを返す
      return { 
        error: true, 
        message: error instanceof Error ? error.message : '不明なエラー',
        details: axios.isAxiosError(error) ? error.response?.data : null,
        orders: []
      };
    }
  },

  async getOrder(id: string | number) {
    if (!id) {
      throw new Error('オーダーIDが指定されていません。');
    }
    console.log(`個別のオーダーを取得します: ${id}`);
    
    try {
      // 実際のAPIリクエスト
      const response = await api.get<Order>(`/orders/${id}`);
      console.log('APIから個別のオーダーデータを取得しました:', response);

      if (response.data && response.data.id) {
        return processOrderData(response.data);
      } else {
        console.error('APIからのオーダーデータが無効です:', response);
        throw new Error('APIからのオーダーデータが無効です。');
      }
    } catch (error) {
      console.error(`オーダーデータの取得に失敗しました (ID: ${id}):`, error);
      if (axios.isAxiosError(error)) {
        console.error('Axiosエラー詳細:', {
          message: error.message,
          response: error.response?.data,
          status: error.response?.status
        });
        throw new Error(`サーバーからの応答エラー: ${error.response?.status || '不明'}`);
      }
      throw new Error('オーダーデータの取得中に不明なエラーが発生しました。');
    }
  },

  async createOrder(orderData: any) {
    try {
      const apiUrl = buildApiUrl('orders');
      console.log('作成URL:', apiUrl);
      console.log('送信データ:', orderData);

      // トークンを取得して分割
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('認証トークンが見つかりません');
        return { error: true, message: '認証トークンがありません' };
      }
      
      // 標準的なBearer認証を使用
      console.log('createOrder - Bearer認証を使用');

      const response = await api.post(apiUrl, orderData, {
        withCredentials: true
      });
      
      console.log('オーダー作成成功:', response.data);
      return response.data;
    } catch (error) {
      console.error('オーダーの作成に失敗:', error);
      
      // エラー詳細をログに記録
      if (axios.isAxiosError(error)) {
        console.error('エラーの詳細:', error.response?.data);
        console.error('ステータスコード:', error.response?.status);
        console.error('エラーメッセージ:', error.message);
        console.error('リクエストURL:', error.config?.url);
      }
      
      // 開発環境ではモックデータを返す
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ エラー発生のため、モックデータを返します');
        const mockOrder = {
          ...orderData,
          id: `mock-${Date.now()}`,
          created_at: new Date().toISOString()
        };
        return { order: mockOrder };
      }
      
      // エラーオブジェクトを返す
      return { 
        error: true, 
        message: error instanceof Error ? error.message : '不明なエラー',
        details: axios.isAxiosError(error) ? error.response?.data : null
      };
    }
  },

  async updateOrder(id: string | number, orderData: any) {
    try {
      const apiUrl = buildApiUrl(`orders/update/${id}`);
      console.log('更新URL:', apiUrl);
      console.log('送信データ:', orderData);

      // トークンを取得して分割
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('認証トークンが見つかりません');
        return { error: true, message: '認証トークンがありません' };
      }
      
      // 標準的なBearer認証を使用
      console.log('updateOrder - Bearer認証を使用');

      const response = await api.put(apiUrl, orderData, {
        withCredentials: true
      });
      
      console.log('オーダー更新成功:', response.data);
      return response.data;
    } catch (error) {
      console.error(`オーダー(ID: ${id})の更新に失敗:`, error);
      
      // エラー詳細をログに記録
      if (axios.isAxiosError(error)) {
        console.error('エラーの詳細:', error.response?.data);
        console.error('ステータスコード:', error.response?.status);
        console.error('エラーメッセージ:', error.message);
        console.error('リクエストURL:', error.config?.url);
      }
      
      // 開発環境ではモックデータを返す
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ エラー発生のため、モックデータを返します');
        const updatedOrder = { ...orderData, id };
        return { order: updatedOrder };
      }
      
      // エラーオブジェクトを返す
      return { 
        error: true, 
        message: error instanceof Error ? error.message : '不明なエラー',
        details: axios.isAxiosError(error) ? error.response?.data : null,
        id
      };
    }
  },

  async deleteOrder(id: string | number) {
    try {
      const apiUrl = buildApiUrl(`orders/${id}`);
      console.log('削除URL:', apiUrl);
      
      // トークンを取得して分割
      const token = localStorage.getItem('token');
      if (!token) {
        console.warn('認証トークンが見つかりません');
        return { error: true, message: '認証トークンがありません' };
      }
      
      // 標準的なBearer認証を使用
      console.log('deleteOrder - Bearer認証を使用');
      
      const response = await api.delete(apiUrl, {
        withCredentials: true
      });
      
      console.log('オーダー削除成功:', response.data);
      return response.data;
    } catch (error) {
      console.error(`オーダー(ID: ${id})の削除に失敗:`, error);
      
      // エラー詳細をログに記録
      if (axios.isAxiosError(error)) {
        console.error('エラーの詳細:', error.response?.data);
        console.error('ステータスコード:', error.response?.status);
        console.error('エラーメッセージ:', error.message);
        console.error('リクエストURL:', error.config?.url);
      }
      
      // 開発環境ではモックデータを返す
      if (process.env.NODE_ENV === 'development') {
        console.log('⚠️ エラー発生のため、モックデータを返します');
        return { success: true, message: 'モックデータによる削除成功' };
      }
      
      // エラーオブジェクトを返す
      return { 
        error: true, 
        message: error instanceof Error ? error.message : '不明なエラー',
        details: axios.isAxiosError(error) ? error.response?.data : null,
        id
      };
    }
  }
};

// 注文を更新する
export const updateOrder = async (id: string | number, orderData: any): Promise<any> => {
  return orderService.updateOrder(id, orderData);
};

// 注文を削除する
export const deleteOrder = async (id: string | number): Promise<any> => {
  return orderService.deleteOrder(id);
}; 